/** IndexedDB is authoritative. The session fallback never claims durability. */
import Dexie, { type Table } from 'dexie';
import { readStoredString as readLegacyString } from '../utils/clientStorage';
import { NEWER_SCHEMA, sanitizeProgressState } from './progressSchema';

export const LEARNER_DATABASE_NAME = 'typewriter-learner';
export const PROGRESS_KEY = 'typewriter_local_progress_v1';
export const STORY_KEY = 'typewriter_story_practice_v1';
export const GAME_KEY = 'game_high_score';
const LEGACY_KEYS = [PROGRESS_KEY, STORY_KEY, GAME_KEY, `${PROGRESS_KEY}.pre-import`];
interface RecordRow { id: string; value: unknown }
interface DocumentRow { key: string; value: string }

class LearnerDatabase extends Dexie {
  documents!: Table<DocumentRow, string>;
  exercises!: Table<RecordRow, string>;
  attempts!: Table<RecordRow, string>;
  drafts!: Table<RecordRow, string>;
  sessions!: Table<RecordRow, string>;
  constructor() {
    super(LEARNER_DATABASE_NAME);
    this.version(1).stores({ documents: '&key', exercises: '&id', attempts: '&id', drafts: '&id', sessions: '&id' });
    // Close old connections so a future schema upgrade is not held by this tab.
    this.on('versionchange', () => this.close());
  }
}
export const learnerDatabase = new LearnerDatabase();

/** Storage is injected into each command; domain code has no database globals. */
export interface LearnerTransaction {
  readonly durable: boolean;
  readonly writable: boolean;
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<boolean>;
  remove(key: string): Promise<void>;
}

export type LearnerStorageStatus = 'indexeddb' | 'session';
let status: LearnerStorageStatus = 'indexeddb';
const listeners = new Set<() => void>();
let channel: BroadcastChannel | undefined;
function notify() { for (const listener of listeners) listener(); }
export function subscribeLearnerChanges(listener: () => void): () => void {
  listeners.add(listener);
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('typewriter-learner-changes');
    channel.onmessage = notify;
  }
  return () => { listeners.delete(listener); };
}
export function getLearnerStorageStatus(): LearnerStorageStatus { return status; }

class IndexedWorkspace implements LearnerTransaction {
  readonly durable = true;
  constructor(readonly writable: boolean) {}
  // A command-local recovery view, only used if its entire transaction aborts.
  readonly recovery = new Map<string, string>();
  async read(key: string): Promise<string | null> {
    let raw = (await learnerDatabase.documents.get(key))?.value ?? null;
    if (key === PROGRESS_KEY && raw) {
      let meta: { split?: boolean };
      try { meta = JSON.parse(raw) as { split?: boolean }; } catch { return raw; }
      if (meta?.split) {
        const [exercises, attempts, drafts, sessions] = await Promise.all([
          learnerDatabase.exercises.toArray(), learnerDatabase.attempts.toArray(),
          learnerDatabase.drafts.toArray(), learnerDatabase.sessions.toArray(),
        ]);
        raw = JSON.stringify({ ...meta, split: undefined,
          exercises: Object.fromEntries(exercises.map(row => [row.id, row.value])),
          attempts: attempts.map(row => row.value).sort((a, b) =>
            String((b as { completedAt: string }).completedAt).localeCompare(String((a as { completedAt: string }).completedAt))),
          drafts: Object.fromEntries(drafts.map(row => [row.id, row.value])),
          openAttempts: Object.fromEntries(sessions.map(row => [row.id, row.value])),
        });
      }
    }
    if (raw !== null) this.recovery.set(key, raw);
    return raw;
  }
  async write(key: string, value: string): Promise<boolean> {
    if (!this.writable) return false;
    if (key === PROGRESS_KEY) {
      const parsed: unknown = JSON.parse(value);
      const state = sanitizeProgressState(parsed);
      if (state === NEWER_SCHEMA) throw new Error('Unsupported learner schema');
      const { exercises, attempts, drafts, openAttempts, ...meta } = state;
      await Promise.all([learnerDatabase.exercises.clear(), learnerDatabase.attempts.clear(),
        learnerDatabase.drafts.clear(), learnerDatabase.sessions.clear()]);
      await Promise.all([
        learnerDatabase.exercises.bulkPut(Object.entries(exercises).map(([id, value]) => ({ id, value }))),
        learnerDatabase.attempts.bulkPut(attempts.map(value => ({ id: value.attemptId, value }))),
        learnerDatabase.drafts.bulkPut(Object.entries(drafts).map(([id, value]) => ({ id, value }))),
        learnerDatabase.sessions.bulkPut(Object.entries(openAttempts).map(([id, value]) => ({ id, value }))),
      ]);
      await learnerDatabase.documents.put({ key, value: JSON.stringify({ ...meta, split: true }) });
    } else await learnerDatabase.documents.put({ key, value });
    return true;
  }
  async remove(key: string): Promise<void> { if (this.writable) await learnerDatabase.documents.delete(key); }
}

class SessionWorkspace implements LearnerTransaction {
  readonly durable = false;
  readonly writable = true;
  constructor(readonly records: Map<string, string>) {}
  async read(key: string): Promise<string | null> { return this.records.get(key) ?? null; }
  async write(key: string, value: string): Promise<boolean> { this.records.set(key, value); return false; }
  async remove(key: string): Promise<void> { this.records.delete(key); }
}
let session: SessionWorkspace | undefined;
let sessionQueue: Promise<unknown> = Promise.resolve();
function getSession(seed = new Map<string, string>()) {
  if (!session) {
    for (const key of LEGACY_KEYS) {
      const raw = readLegacyString(key);
      if (raw !== null && !seed.has(key)) seed.set(key, raw);
    }
    session = new SessionWorkspace(seed);
  }
  status = 'session';
  return session;
}

async function migrateLegacy() {
  if (await learnerDatabase.documents.get('legacy-migrated')) return;
  await learnerDatabase.transaction('rw', learnerDatabase.tables, async () => {
    if (await learnerDatabase.documents.get('legacy-migrated')) return;
    // Preserve source bytes and mark migration only in the same committed transaction.
    // Leave localStorage intact: it is a recovery copy, never a second active store.
    for (const key of LEGACY_KEYS) {
      const raw = readLegacyString(key);
      if (raw !== null) {
        await learnerDatabase.documents.put({ key, value: raw });
        await learnerDatabase.documents.put({ key: `legacy-backup:${key}`, value: raw });
      }
    }
    await learnerDatabase.documents.put({ key: 'legacy-migrated', value: '1' });
  });
}

function isStorageFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('name' in error)) return false;
  if (['QuotaExceededError', 'UnknownError', 'SecurityError', 'InvalidStateError', 'DatabaseClosedError'].includes(String(error.name))) return true;
  // Dexie aggregates bulk operation errors. Only replay when every failed
  // operation is a storage failure; constraint/domain errors must propagate.
  if (error.name === 'BulkError' && 'failures' in error && Array.isArray(error.failures)) {
    return error.failures.length > 0 && error.failures.every(isStorageFailure);
  }
  return false;
}

/** A callback may replay once after an aborted storage transaction. Keep all
 * effects inside the store; capture externally meaningful IDs/times before
 * invoking this function. Notifications occur only after the final outcome.
 * Session fallback is latched: subsequent commands never retry IndexedDB. */
export async function withLearnerStorage<T>(mode: 'r' | 'rw', operation: (store: LearnerTransaction) => Promise<T>): Promise<T> {
  const runSession = (store: SessionWorkspace) => {
    const run = sessionQueue.then(() => operation(store));
    sessionQueue = run.catch(() => undefined);
    return run;
  };
  if (session) return runSession(session);
  const store = new IndexedWorkspace(mode === 'rw');
  try {
    await migrateLegacy();
  } catch {
    const result = await runSession(getSession());
    notify();
    return result;
  }
  try {
    const result = await learnerDatabase.transaction(mode, learnerDatabase.tables, async () => {
      // Capture a coherent recovery view before mutations; a quota failure must
      // not revive stale legacy values for modes this command did not read.
      if (mode === 'rw') {
        for (const key of LEGACY_KEYS) await store.read(key);
      }
      return operation(store);
    });
    if (mode === 'rw') { notify(); channel?.postMessage('committed'); }
    return result;
  } catch (error) {
    // Domain validation errors must not switch storage modes or replay commands.
    if (!isStorageFailure(error)) throw error;
    const result = await runSession(getSession(store.recovery));
    notify();
    return result;
  }
}

export async function readStoredString(store: LearnerTransaction, key: string) { return store.read(key); }
export async function writeStoredString(store: LearnerTransaction, key: string, value: string) { return store.write(key, value); }
export async function writeStoredJson(store: LearnerTransaction, key: string, value: unknown) { return store.write(key, JSON.stringify(value)); }
export async function removeStoredKey(store: LearnerTransaction, key: string) { return store.remove(key); }
